import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  FolderOpen, FileSearch, FlaskConical, ClipboardCheck, Stamp,
  Download, ChevronRight, Sparkles, ArrowUpRight,
} from "lucide-react";

const STAT_DEFS = [
  { key: "total_cases", label: "Total Cases", Icon: FolderOpen, tint: "text-slate-700" },
  { key: "drafting_pending", label: "Drafting Pending", Icon: FileSearch, tint: "text-amber-700" },
  { key: "documents_processing", label: "Documents Processing", Icon: FlaskConical, tint: "text-blue-700" },
  { key: "in_review", label: "In Review", Icon: ClipboardCheck, tint: "text-violet-700" },
  { key: "approved_drafts", label: "Approved Drafts", Icon: Stamp, tint: "text-emerald-700" },
  { key: "exported_orders", label: "Exported Orders", Icon: Download, tint: "text-emerald-700" },
];

const PROCESS_STEPS = [
  "Case Input", "Document Processing", "Knowledge Reference",
  "Remi8 Drafting", "Human Review", "Export",
];

export default function Dashboard() {
  const [data, setData] = useState({ summary: {}, recent_cases: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout breadcrumbs={["SSRD", "Dashboard"]}>
      <div className="flex items-start justify-between mb-6">
        <div className="space-y-1">
          <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Overview</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Operational view of active SSRD appeals and the Remi8 drafting pipeline.
          </p>
        </div>
        <Link to="/cases/new" data-testid="new-case-cta">
          <Button className="gap-2"><Sparkles className="size-4" /> New Case</Button>
        </Link>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="summary-cards">
        {STAT_DEFS.map(({ key, label, Icon, tint }) => (
          <div key={key} className="stat-card" data-testid={`stat-${key}`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-widest text-muted-foreground uppercase">{label}</div>
              <Icon className={`size-4 ${tint}`} />
            </div>
            <div className="mt-2 text-3xl font-bold tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
              {loading ? "—" : (data.summary[key] ?? 0)}
            </div>
          </div>
        ))}
      </div>

      {/* Pipeline */}
      <Card className="mb-8" data-testid="pipeline-widget">
        <CardContent className="p-5">
          <div className="text-[10px] tracking-widest text-muted-foreground uppercase mb-3">Process Status Pipeline</div>
          <ol className="flex flex-wrap items-center gap-2 md:gap-3">
            {PROCESS_STEPS.map((s, i) => (
              <li key={s} className="flex items-center gap-2">
                <span className="rounded-md border border-border bg-secondary/40 px-3 py-1.5 text-xs font-medium flex items-center gap-2">
                  <span className="text-[10px] font-mono text-muted-foreground">0{i + 1}</span> {s}
                </span>
                {i < PROCESS_STEPS.length - 1 && <ChevronRight className="size-3.5 text-muted-foreground" />}
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {/* Recent Cases */}
      <Card data-testid="recent-cases-card">
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Recent</div>
              <div className="text-lg font-semibold">Cases</div>
            </div>
            <Link to="/cases" className="text-xs text-primary inline-flex items-center gap-1 hover:underline">
              View all <ArrowUpRight className="size-3" />
            </Link>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Case Number</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>District</TableHead>
                <TableHead>Applicant</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recent_cases.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">No cases yet.</TableCell></TableRow>
              )}
              {data.recent_cases.map((c) => (
                <TableRow key={c.case_id} data-testid={`recent-case-row-${c.case_id}`}>
                  <TableCell className="font-mono text-xs">{c.case_number}/{c.case_year}</TableCell>
                  <TableCell>{c.case_type}</TableCell>
                  <TableCell>{c.district}</TableCell>
                  <TableCell>{c.applicant || "—"}</TableCell>
                  <TableCell><StatusBadge status={c.case_status} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(c.updated_at || "").slice(0,10)}</TableCell>
                  <TableCell className="text-right">
                    <Link to={`/cases/${c.case_id}`} data-testid={`open-case-${c.case_id}`}>
                      <Button variant="outline" size="sm">Open Case</Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Layout>
  );
}
